import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ArrowDownward,
    ArrowUpward,
    Delete,
    VerticalAlignBottom,
    VerticalAlignTop,
} from '@mui/icons-material';
import {
    ReservationService
} from "crqlar-react-common/dist/services/reservation/reservation.service";
import CMemoryColorPicker
    from 'crqlar-react-common/dist/components/CMemoryColorPicker';
import {FabricObject} from 'fabric';
import {
    getTableCapacity,
    dashArrayToStrokeStyle,
    StrokeStyle,
    strokeStyleToDashArray
} from '../utils/fabric-factory';
import {TABLE_MIN_SIZE} from "../utils/constants";
import {FABRIC_DEFAULTS} from "../utils/fabric-defaults";
import {useEditConfiguration} from "../hooks/useEditConfiguration";
import CNumberField from "crqlar-react-common/dist/components/CNumberField";
import {useFloorplanContext} from "../hooks/useFloorplanContext";
import {useLiveScaledDimensions} from "../hooks/useLiveScaledDimensions";
import {
    getNormalizedTableCanvas,
    getNormalizedTableDimensions,
} from "../utils/resize-utils";

interface Props {
    element: FabricObject;
    onBack: () => void;
    onElementUpdated: () => void;
}

export const EditTableStyles: React.FC<Props> = ({
                                                     element,
                                                     onBack,
                                                     onElementUpdated,
                                                 }) => {
    const {t} = useTranslation();
    const {canvas, updateTableElementLocally} = useFloorplanContext();

    const item = element.floorPlanItem!;
    const tableData = item.table!;
    const tableCanvas = tableData.canvas;

    const {
        position,
        handleUpdatePosition,
        rotation,
        handleRotationChange,
        updateSelectable,
        handleBringToFront,
        handleSendToBack,
        handleBringForward,
        handleSendBackward,
        handleDelete,
    } = useEditConfiguration({canvas, element, onBack, onElementUpdated});

    const [localTableName, setLocalTableName] = useState(tableData.resource?.name ?? '');

    useEffect(() => {
        setLocalTableName(tableData.resource?.name ?? '');
    }, [item.id, tableData.resource?.name]);

    const handleUpdateTableConfig = useCallback((updates: {
        canvas?: Record<string, any>;
        resource?: { name?: string };
        seats?: Record<string, any>;
    }) => {
        const nextElement = updateTableElementLocally(element, (currentItem, currentElement) => {
            const currentTable = currentItem.table!;
            const scaledCanvas = getNormalizedTableCanvas(
                currentTable.canvas,
                currentTable.shape,
                currentElement.scaleX,
                currentElement.scaleY
            );
            const mergedSeats = updates.seats ? {...currentTable.seats, ...updates.seats} as any : currentTable.seats;
            const mergedResource = {
                ...(currentTable.resource ?? {}),
                ...(updates.resource?.name ? {name: updates.resource.name} : {}),
                ...(mergedSeats ? {capacity: getTableCapacity(mergedSeats, currentTable.resource?.capacity ?? 0)} : {}),
            } as any;

            return {
                ...currentItem,
                table: {
                    ...currentTable,
                    canvas: {...scaledCanvas, ...updates.canvas} as any,
                    resource: mergedResource,
                    seats: mergedSeats,
                },
            };
        });
        if (!nextElement) return;
        onElementUpdated();
    }, [element, onElementUpdated, updateTableElementLocally]);

    const getDimensions = useCallback((target: FabricObject) => {
        const targetTable = target.floorPlanItem?.table ?? tableData;
        return getNormalizedTableDimensions(
            targetTable.canvas,
            targetTable.shape,
            target.scaleX,
            target.scaleY
        );
    }, [tableData]);

    const scalingOverride = useLiveScaledDimensions(canvas, element, getDimensions);
    const dimensions = scalingOverride ?? getDimensions(element);

    const handleUpdateTableDimensions = useCallback((width: number, height: number) => {
        const nextElement = updateTableElementLocally(element, (currentItem, currentElement) => {
            const currentTable = currentItem.table!;
            const scaledCanvas = getNormalizedTableCanvas(
                currentTable.canvas,
                currentTable.shape,
                currentElement.scaleX,
                currentElement.scaleY
            );

            return {
                ...currentItem,
                table: {
                    ...currentTable,
                    canvas: {...scaledCanvas, width, height} as any
                },
            };
        });
        if (!nextElement) return;
        onElementUpdated();
    }, [element, onElementUpdated, updateTableElementLocally]);

    const handleSelectableChange = useCallback((selectable: boolean) => {
        updateSelectable(selectable);
        handleUpdateTableConfig({canvas: {selectable}});
    }, [updateSelectable, handleUpdateTableConfig]);

    const handleTableNameBlur = useCallback(() => {
        const trimmed = localTableName.trim();
        if (trimmed && trimmed !== tableData.resource?.name) {
            handleUpdateTableConfig({resource: {name: trimmed}});
        } else {
            setLocalTableName(tableData.resource?.name ?? '');
        }
    }, [localTableName, tableData.resource?.name, handleUpdateTableConfig]);

    const [deleteDialog, setDeleteDialog] = useState<{
        open: boolean;
        loading: boolean;
        hasReservations: boolean
    }>({
        open: false, loading: false, hasReservations: false,
    });

    const handleDeleteClick = useCallback(async () => {
        const resourceId = tableData.resource?.id;
        if (!resourceId) {
            handleDelete();
            return;
        }

        setDeleteDialog({open: true, loading: true, hasReservations: false});
        try {
            const result = await ReservationService.getAllReservations({
                resourceIds: [resourceId],
                limit: {offset: 0, limit: 1},
            });
            setDeleteDialog({
                open: true,
                loading: false,
                hasReservations: (result.totalElements ?? 0) > 0
            });
        } catch {
            setDeleteDialog({open: true, loading: false, hasReservations: false});
        }
    }, [tableData.resource?.id, handleDelete]);

    const isDisabled = tableCanvas?.selectable === false;
    const currentStrokeStyle = dashArrayToStrokeStyle(tableCanvas?.strokeDashArray as number[] | undefined);

    return (
        <Stack spacing={2.5}>
            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Table Name')}</Typography>
                <TextField
                    size="small"
                    fullWidth
                    value={localTableName}
                    onChange={(e) => setLocalTableName(e.target.value)}
                    onBlur={handleTableNameBlur}
                    onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                    disabled={isDisabled}
                    sx={{
                        '& .MuiInputBase-root': {
                            height: 40,
                        },
                    }}
                />
            </Box>

            <Box>
                <Typography variant="subtitle2"
                            sx={{mb: 1.5}}>{t('Position')}</Typography>
                <Stack direction="row" spacing={1}>
                    <CNumberField
                        size="small"
                        label={t('Left')}
                        value={Math.round(position.x)}
                        onChange={(v: number | null) => v !== null && handleUpdatePosition(v, position.y)}
                        sx={{flex: 1}}
                        disabled={isDisabled}
                    />
                    <CNumberField
                        size="small"
                        label={t('Top')}
                        value={Math.round(position.y)}
                        onChange={(v: number | null) => v !== null && handleUpdatePosition(position.x, v)}
                        sx={{flex: 1}}
                        disabled={isDisabled}
                    />
                </Stack>
            </Box>

            {tableData.shape === 'circle' ? (
                <Box>
                    <Typography variant="subtitle2"
                                sx={{mb: 1.5}}>{t('Size')}</Typography>
                    <CNumberField
                        size="small"
                        label={t('Diameter')}
                        value={Math.round(Math.min(dimensions.width, dimensions.height))}
                        onChange={(v: number | null) => v !== null && v >= TABLE_MIN_SIZE && handleUpdateTableDimensions(v, v)}
                        min={TABLE_MIN_SIZE}
                        sx={{width: 'calc(50% - 4px)'}}
                        disabled={isDisabled}
                    />
                </Box>
            ) : (
                <Box>
                    <Typography variant="subtitle2"
                                sx={{mb: 1.5}}>{t('Size')}</Typography>
                    <Stack direction="row" spacing={1}>
                        <CNumberField
                            size="small"
                            label={t('Width')}
                            value={Math.round(dimensions.width)}
                            onChange={(v: number | null) => v !== null && v >= TABLE_MIN_SIZE && handleUpdateTableDimensions(v, Math.round(dimensions.height))}
                            min={TABLE_MIN_SIZE}
                            sx={{flex: 1}}
                            disabled={isDisabled}
                        />
                        <CNumberField
                            size="small"
                            label={t('Height')}
                            value={Math.round(dimensions.height)}
                            onChange={(v: number | null) => v !== null && v >= TABLE_MIN_SIZE && handleUpdateTableDimensions(Math.round(dimensions.width), v)}
                            min={TABLE_MIN_SIZE}
                            sx={{flex: 1}}
                            disabled={isDisabled}
                        />
                    </Stack>
                </Box>
            )}

            {tableData.shape !== 'circle' && (
                <Box>
                    <Typography variant="subtitle2"
                                gutterBottom>{t('Rotation')}</Typography>
                    <CNumberField
                        size="small"
                        fullWidth
                        value={Math.round(rotation)}
                        onChange={(v: number | null) => handleRotationChange(v ?? undefined)}
                        min={-360}
                        max={360}
                        disabled={isDisabled}
                    />
                </Box>
            )}

            <Divider/>

            <Stack direction="row" spacing={1.5}
                   sx={{opacity: isDisabled ? 0.5 : 1}}>
                <Box>
                    <Typography variant="subtitle2"
                                gutterBottom>{t('Fill')}</Typography>
                    <CMemoryColorPicker
                        color={tableCanvas?.fill as string ?? FABRIC_DEFAULTS.table.fill}
                        onChange={(color) => handleUpdateTableConfig({canvas: {fill: color}})}
                        disabled={isDisabled}
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle2"
                                gutterBottom>{t('Stroke')}</Typography>
                    <CMemoryColorPicker
                        color={tableCanvas?.stroke as string ?? FABRIC_DEFAULTS.table.stroke}
                        onChange={(color) => handleUpdateTableConfig({canvas: {stroke: color}})}
                        disabled={isDisabled}
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle2"
                                gutterBottom>{t('Text')}</Typography>
                    <CMemoryColorPicker
                        color={tableCanvas?.textColor ?? FABRIC_DEFAULTS.table.textColor}
                        onChange={(color) => handleUpdateTableConfig({canvas: {textColor: color}})}
                        disabled={isDisabled}
                    />
                </Box>
            </Stack>

            <Divider/>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Stroke Width')}</Typography>
                <CNumberField
                    size="small"
                    fullWidth
                    value={tableCanvas?.strokeWidth ?? FABRIC_DEFAULTS.table.strokeWidth}
                    onChange={(v: number | null) => v !== null && v >= 0 && v <= 10 && handleUpdateTableConfig({canvas: {strokeWidth: v}})}
                    min={0}
                    max={10}
                    disabled={isDisabled}
                />
            </Box>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Stroke Style')}</Typography>
                <Select
                    size="small"
                    fullWidth
                    value={currentStrokeStyle}
                    onChange={(e) => handleUpdateTableConfig({canvas: {strokeDashArray: strokeStyleToDashArray(e.target.value as StrokeStyle)}})}
                    disabled={isDisabled}
                >
                    <MenuItem value="solid">{t('Solid')}</MenuItem>
                    <MenuItem value="dashed">{t('Dashed')}</MenuItem>
                    <MenuItem value="dotted">{t('Dotted')}</MenuItem>
                    <MenuItem value="long-dashed">{t('Long Dashed')}</MenuItem>
                </Select>
            </Box>

            {tableData.shape === 'rectangle' && (
                <Box>
                    <Typography variant="subtitle2"
                                gutterBottom>{t('Corner Radius')}</Typography>
                    <CNumberField
                        size="small"
                        fullWidth
                        value={(tableCanvas as any)?.rx ?? FABRIC_DEFAULTS.table.cornerRadius}
                        onChange={(v: number | null) => v !== null && v >= 0 && v <= 50 && handleUpdateTableConfig({
                            canvas: {
                                rx: v,
                                ry: v
                            }
                        })}
                        min={0}
                        max={50}
                        disabled={isDisabled}
                    />
                </Box>
            )}

            <Divider/>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Interaction')}</Typography>
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={tableCanvas?.selectable !== false}
                            onChange={(e) => handleSelectableChange(e.target.checked)}
                        />
                    }
                    label={<Typography
                        variant="body2">{t('Selectable on canvas')}</Typography>}
                />
            </Box>

            <Divider/>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Layer')}</Typography>
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title={t('Bring to Front')}>
                        <span>
                            <IconButton size="small" onClick={handleBringToFront}
                                        disabled={isDisabled}>
                                <VerticalAlignTop fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('Bring Forward')}>
                        <span>
                            <IconButton size="small" onClick={handleBringForward}
                                        disabled={isDisabled}>
                                <ArrowUpward fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('Send Backward')}>
                        <span>
                            <IconButton size="small" onClick={handleSendBackward}
                                        disabled={isDisabled}>
                                <ArrowDownward fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('Send to Back')}>
                        <span>
                            <IconButton size="small" onClick={handleSendToBack}
                                        disabled={isDisabled}>
                                <VerticalAlignBottom fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Box>

            <Divider/>

            <Box>
                <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<Delete/>}
                    onClick={handleDeleteClick}
                >
                    {t('Delete Table')}
                </Button>
            </Box>

            <Dialog open={deleteDialog.open}
                    onClose={() => setDeleteDialog(d => ({...d, open: false}))}
                    maxWidth="xs" fullWidth>
                <DialogTitle>{t('Delete Table')}</DialogTitle>
                <DialogContent>
                    {deleteDialog.loading ? (
                        <Box
                            sx={{display: 'flex', justifyContent: 'center', py: 2}}>
                            <CircularProgress size={24}/>
                        </Box>
                    ) : deleteDialog.hasReservations ? (
                        <Alert severity="warning" variant="outlined">
                            {t('This table has existing reservations. Deleting it will remove the table assignment from those reservations.')}
                        </Alert>
                    ) : (
                        <Typography>{t('Are you sure you want to delete this table?')}</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button onClick={() => setDeleteDialog(d => ({
                        ...d,
                        open: false
                    }))}>{t('Cancel')}</Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={deleteDialog.loading}
                        onClick={() => {
                            setDeleteDialog(d => ({...d, open: false}));
                            handleDelete();
                        }}
                    >
                        {t('Delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};
